<!-- CreateRouteDialog.vue -->
<!-- Dialog para crear nueva ruta de visitas -->

<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="800px">
    <v-card>
      <v-card-title>
        <span class="text-h5">Crear Nueva Ruta de Visitas</span>
      </v-card-title>

      <v-card-text>
        <v-stepper v-model="currentStep" alt-labels>
          <v-stepper-header>
            <v-stepper-item
              title="Información Básica"
              value="1"
              :complete="currentStep > 1"
            />
            <v-divider />
            <v-stepper-item
              title="Seleccionar Agencias"
              value="2"
              :complete="currentStep > 2"
            />
            <v-divider />
            <v-stepper-item
              title="Optimizar Ruta"
              value="3"
              :complete="currentStep > 3"
            />
            <v-divider />
            <v-stepper-item
              title="Confirmar"
              value="4"
            />
          </v-stepper-header>

          <v-stepper-window>
            <!-- Step 1: Información Básica -->
            <v-stepper-window-item value="1">
              <v-form ref="basicInfoForm" v-model="basicInfoValid">
                <v-text-field
                  v-model="routeData.name"
                  label="Nombre de la Ruta"
                  :rules="[rules.required]"
                  required
                />
                <v-textarea
                  v-model="routeData.description"
                  label="Descripción (Opcional)"
                  rows="3"
                />
                <v-select
                  v-model="routeData.type"
                  :items="routeTypes"
                  label="Tipo de Ruta"
                  :rules="[rules.required]"
                  required
                />
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="routeData.startTime"
                      label="Hora de Inicio"
                      type="time"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="routeData.estimatedDuration"
                      label="Duración Estimada (horas)"
                      type="number"
                      min="1"
                      max="12"
                      :rules="[rules.required, rules.number]"
                      required
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-stepper-window-item>

            <!-- Step 2: Seleccionar Agencias -->
            <v-stepper-window-item value="2">
              <div class="mb-4">
                <v-text-field
                  v-model="agencySearch"
                  label="Buscar agencias"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                />
              </div>

              <v-row>
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title>Agencias Disponibles</v-card-title>
                    <v-card-text style="max-height: 300px; overflow-y: auto;">
                      <v-list>
                        <v-list-item
                          v-for="agency in filteredAgencies"
                          :key="agency.id"
                          @click="addAgencyToRoute(agency)"
                          :disabled="isAgencySelected(agency)"
                        >
                          <template #prepend>
                            <v-icon 
                              :color="agency.status === 'operational' ? 'success' : 'warning'"
                              :icon="agency.status === 'operational' ? 'mdi-check-circle' : 'mdi-alert'"
                            />
                          </template>
                          <v-list-item-title>{{ agency.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ agency.address }}</v-list-item-subtitle>
                          <template #append>
                            <v-btn
                              icon="mdi-plus"
                              size="small"
                              variant="text"
                              :disabled="isAgencySelected(agency)"
                            />
                          </template>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title>
                      Ruta Seleccionada ({{ routeData.agencies.length }})
                    </v-card-title>
                    <v-card-text style="max-height: 300px; overflow-y: auto;">
                      <v-list>
                        <v-list-item
                          v-for="(agency, index) in routeData.agencies"
                          :key="agency.id"
                        >
                          <template #prepend>
                            <v-chip size="small" color="primary">{{ index + 1 }}</v-chip>
                          </template>
                          <v-list-item-title>{{ agency.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ agency.address }}</v-list-item-subtitle>
                          <template #append>
                            <v-btn-group variant="text" size="small">
                              <v-btn
                                icon="mdi-arrow-up"
                                :disabled="index === 0"
                                @click="moveAgencyUp(index)"
                              />
                              <v-btn
                                icon="mdi-arrow-down"
                                :disabled="index === routeData.agencies.length - 1"
                                @click="moveAgencyDown(index)"
                              />
                              <v-btn
                                icon="mdi-delete"
                                @click="removeAgencyFromRoute(index)"
                              />
                            </v-btn-group>
                          </template>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-stepper-window-item>

            <!-- Step 3: Optimizar Ruta -->
            <v-stepper-window-item value="3">
              <v-alert type="info" class="mb-4">
                <v-alert-title>Optimización de Ruta</v-alert-title>
                La ruta será optimizada automáticamente para minimizar el tiempo de viaje.
              </v-alert>

              <v-row>
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title>Configuración de Optimización</v-card-title>
                    <v-card-text>
                      <v-select
                        v-model="routeData.optimization"
                        :items="optimizationOptions"
                        label="Criterio de Optimización"
                      />
                      <v-checkbox
                        v-model="routeData.avoidTraffic"
                        label="Evitar tráfico pesado"
                      />
                      <v-checkbox
                        v-model="routeData.considerPriority"
                        label="Considerar prioridad de agencias"
                      />
                      <v-text-field
                        v-model="routeData.maxVisitTime"
                        label="Tiempo máximo por visita (minutos)"
                        type="number"
                        min="15"
                        max="120"
                      />
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title>Resumen de la Ruta</v-card-title>
                    <v-card-text>
                      <v-list-item>
                        <template #prepend>
                          <v-icon icon="mdi-map-marker-multiple" />
                        </template>
                        <v-list-item-title>
                          {{ routeData.agencies.length }} agencias seleccionadas
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item>
                        <template #prepend>
                          <v-icon icon="mdi-clock" />
                        </template>
                        <v-list-item-title>
                          Duración estimada: {{ calculateEstimatedTime() }}
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item>
                        <template #prepend>
                          <v-icon icon="mdi-car" />
                        </template>
                        <v-list-item-title>
                          Distancia aproximada: {{ calculateDistance() }}
                        </v-list-item-title>
                      </v-list-item>

                      <v-btn
                        color="primary"
                        @click="optimizeRoute"
                        :loading="optimizing"
                        block
                        class="mt-4"
                      >
                        <v-icon start>mdi-auto-fix</v-icon>
                        Optimizar Ruta
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-stepper-window-item>

            <!-- Step 4: Confirmar -->
            <v-stepper-window-item value="4">
              <v-alert type="success" class="mb-4">
                <v-alert-title>Ruta Lista para Crear</v-alert-title>
                Revisa la información antes de confirmar la creación de la ruta.
              </v-alert>

              <v-card variant="outlined">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <h4>Información General</h4>
                      <v-list density="compact">
                        <v-list-item>
                          <v-list-item-title>Nombre:</v-list-item-title>
                          <v-list-item-subtitle>{{ routeData.name }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-title>Tipo:</v-list-item-title>
                          <v-list-item-subtitle>{{ routeData.type }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-title>Hora de inicio:</v-list-item-title>
                          <v-list-item-subtitle>{{ routeData.startTime }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-title>Duración:</v-list-item-title>
                          <v-list-item-subtitle>{{ routeData.estimatedDuration }} horas</v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-col>

                    <v-col cols="12" md="6">
                      <h4>Agencias en la Ruta</h4>
                      <v-list density="compact">
                        <v-list-item
                          v-for="(agency, index) in routeData.agencies"
                          :key="agency.id"
                        >
                          <template #prepend>
                            <v-chip size="small" color="primary">{{ index + 1 }}</v-chip>
                          </template>
                          <v-list-item-title>{{ agency.name }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn @click="$emit('update:modelValue', false)">
          Cancelar
        </v-btn>
        <v-btn 
          v-if="currentStep > 1" 
          @click="currentStep--"
        >
          Anterior
        </v-btn>
        <v-btn
          v-if="currentStep < 4"
          color="primary"
          @click="nextStep"
          :disabled="!canProceedToNextStep"
        >
          Siguiente
        </v-btn>
        <v-btn
          v-if="currentStep === 4"
          color="primary"
          @click="createRoute"
          :loading="creating"
        >
          Crear Ruta
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineEmits } from 'vue'

interface Agency {
  id: number
  name: string
  address: string
  status: string
}

interface Props {
  modelValue: boolean
  agencies: Agency[]
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue', 'route-created'])

const currentStep = ref(1)
const basicInfoValid = ref(false)
const agencySearch = ref('')
const optimizing = ref(false)
const creating = ref(false)

const routeData = ref({
  name: '',
  description: '',
  type: 'inspection',
  startTime: '08:00',
  estimatedDuration: 4,
  agencies: [] as Agency[],
  optimization: 'time',
  avoidTraffic: true,
  considerPriority: true,
  maxVisitTime: 30
})

const rules = {
  required: (value: string) => !!value || 'Este campo es requerido',
  number: (value: string) => (!value || !isNaN(Number(value))) || 'Debe ser un número válido'
}

const routeTypes = [
  { title: 'Inspección', value: 'inspection' },
  { title: 'Mantenimiento', value: 'maintenance' },
  { title: 'Cobranza', value: 'collection' },
  { title: 'Supervisión', value: 'supervision' },
  { title: 'Emergencia', value: 'emergency' }
]

const optimizationOptions = [
  { title: 'Tiempo mínimo', value: 'time' },
  { title: 'Distancia mínima', value: 'distance' },
  { title: 'Prioridad de agencias', value: 'priority' },
  { title: 'Menor tráfico', value: 'traffic' }
]

const filteredAgencies = computed(() => {
  if (!agencySearch.value) return props.agencies
  
  return props.agencies.filter(agency =>
    agency.name.toLowerCase().includes(agencySearch.value.toLowerCase()) ||
    agency.address.toLowerCase().includes(agencySearch.value.toLowerCase())
  )
})

const canProceedToNextStep = computed(() => {
  switch (currentStep.value) {
    case 1:
      return basicInfoValid.value
    case 2:
      return routeData.value.agencies.length > 0
    case 3:
      return true
    default:
      return false
  }
})

const isAgencySelected = (agency: Agency) => {
  return routeData.value.agencies.some(a => a.id === agency.id)
}

const addAgencyToRoute = (agency: Agency) => {
  if (!isAgencySelected(agency)) {
    routeData.value.agencies.push(agency)
  }
}

const removeAgencyFromRoute = (index: number) => {
  routeData.value.agencies.splice(index, 1)
}

const moveAgencyUp = (index: number) => {
  if (index > 0) {
    const agencies = routeData.value.agencies
    const temp = agencies[index]
    agencies[index] = agencies[index - 1]
    agencies[index - 1] = temp
  }
}

const moveAgencyDown = (index: number) => {
  const agencies = routeData.value.agencies
  if (index < agencies.length - 1) {
    const temp = agencies[index]
    agencies[index] = agencies[index + 1]
    agencies[index + 1] = temp
  }
}

const calculateEstimatedTime = () => {
  const baseTime = routeData.value.agencies.length * (routeData.value.maxVisitTime || 30)
  const travelTime = routeData.value.agencies.length * 15 // 15 min promedio entre agencias
  const totalMinutes = baseTime + travelTime
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}h ${minutes}m`
}

const calculateDistance = () => {
  // Cálculo aproximado: 2km promedio entre agencias
  const approxDistance = routeData.value.agencies.length * 2
  return `${approxDistance} km (aproximado)`
}

const optimizeRoute = async () => {
  optimizing.value = true
  alert('La optimización de rutas está pendiente de backend real con alcance scoped.')
  optimizing.value = false
}

const nextStep = () => {
  if (canProceedToNextStep.value) {
    currentStep.value++
  }
}

const createRoute = async () => {
  creating.value = true
  
  try {
    alert('La creación de rutas de supervisor está pendiente de backend real con alcance scoped.')
  } catch (error) {
    console.error('Error creando ruta:', error)
  } finally {
    creating.value = false
  }
}

const resetForm = () => {
  currentStep.value = 1
  routeData.value = {
    name: '',
    description: '',
    type: 'inspection',
    startTime: '08:00',
    estimatedDuration: 4,
    agencies: [],
    optimization: 'time',
    avoidTraffic: true,
    considerPriority: true,
    maxVisitTime: 30
  }
}

// Reset form when dialog closes
watch(() => props.modelValue, (newVal) => {
  if (!newVal) {
    resetForm()
  }
})
</script>

<style scoped>
.v-stepper-window {
  margin: 16px 0;
}
</style>
